import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Divider,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
} from '@mui/material';
import AddressForm from './address_form';

const OFFICER_ROLES = [
  { value: 'director', label: 'Director' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'member', label: 'LLP Member' },
];

const OfficerAddDialog = ({
  open,
  onClose,
  onAdd,
  companyAddress,
  companyType,
}) => {
  const [officer, setOfficer] = useState({
    role:
      companyType === 'Limited Liability Partnership' ? 'member' : 'director',
    name: '',
    nationality: '',
    dateOfBirth: '',
    address: { ...companyAddress }, // Initialize with company address
  });

  const [useDifferentAddress, setUseDifferentAddress] = useState(false);
  const [errors, setErrors] = useState({});

  // Filter roles based on company type
  const getAvailableRoles = () => {
    if (companyType === 'Limited Liability Partnership') {
      return OFFICER_ROLES.filter((role) => role.value === 'member');
    }
    if (companyType === 'Public Limited Company') {
      return OFFICER_ROLES.filter((role) => role.value !== 'member');
    }
    return OFFICER_ROLES.filter((role) => role.value !== 'member');
  };

  const handleChange = (field, value) => {
    setOfficer((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field, value) => {
    setOfficer((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!officer.name.trim()) newErrors.name = 'Name is required';
    if (!officer.nationality.trim())
      newErrors.nationality = 'Nationality is required';
    if (!officer.dateOfBirth)
      newErrors.dateOfBirth = 'Date of birth is required';
    if (!officer.role) newErrors.role = 'Role is required';

    if (useDifferentAddress) {
      if (!officer.address.street)
        newErrors['address.street'] = 'Street address is required';
      if (!officer.address.city) newErrors['address.city'] = 'City is required';
      if (!officer.address.postalCode)
        newErrors['address.postalCode'] = 'Postcode is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (validate()) {
      // If using same address, ensure we're using the latest company address
      const finalOfficer = {
        ...officer,
        address: useDifferentAddress ? officer.address : { ...companyAddress },
      };
      onAdd(finalOfficer);
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setOfficer({
      role:
        companyType === 'Limited Liability Partnership' ? 'member' : 'director',
      name: '',
      nationality: '',
      dateOfBirth: '',
      address: { ...companyAddress },
    });
    setUseDifferentAddress(false);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleAddress = (checked) => {
    setUseDifferentAddress(checked);
    if (checked) {
      // Clear address when user wants to enter different address
      setOfficer((prev) => ({
        ...prev,
        address: {
          street: '',
          city: '',
          postalCode: '',
          country: companyAddress.country || 'United Kingdom',
        },
      }));
    } else {
      // Reset to company address when unchecked
      setOfficer((prev) => ({
        ...prev,
        address: { ...companyAddress },
      }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Add Company Officer</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage a new designated role in the Company.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3} sx={{ pt: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full Name *"
              value={officer.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors?.name}
              helperText={errors?.name}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nationality *"
              value={officer.nationality}
              onChange={(e) => handleChange('nationality', e.target.value)}
              error={!!errors?.nationality}
              helperText={errors?.nationality}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date of Birth *"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={officer.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              error={!!errors?.dateOfBirth}
              helperText={errors?.dateOfBirth}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Role *</InputLabel>
              <Select
                value={officer.role}
                label="Role *"
                onChange={(e) => handleChange('role', e.target.value)}
                error={!!errors?.role}
              >
                {getAvailableRoles().map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useDifferentAddress}
                    onChange={(e) => toggleAddress(e.target.checked)}
                    color="primary"
                  />
                }
                label="Address differs from registered office address"
              />
              <Divider sx={{ flexGrow: 1 }} />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Officer Address
            </Typography>
            <Grid container spacing={2}>
              <AddressForm
                address={officer.address}
                onAddressChange={handleAddressChange}
                disabled={!useDifferentAddress}
                errors={{
                  street: errors?.['address.street'],
                  city: errors?.['address.city'],
                  postalCode: errors?.['address.postalCode'],
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          color="primary"
          variant="contained"
          size="large"
        >
          Add Officer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfficerAddDialog;
